import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '..'
import {
  BiomarkModelType,
  FlowCytometryReviewAnalysisSeverity,
  OfrBlockType,
  QualityReviewDeviationType,
  RiskReliefDeviationRisk,
  RiskReliefDeviationSeverity,
  WorkspaceDocumentSetStatus,
} from '../../constants/constants'
import {
  AlgorithmRunStatus,
  MissingReferenceCategory,
  PrivacyType,
} from '../../types/generalTypes'
import { DataSource } from './dataSourceReducer'
import { DataStore } from './dataStoreReducer'
import { OfrBlockInfo } from './ofrDocumentTemplateReducer'
import { User } from './userReducer'
export interface WorkspaceFiles {
  id: string
  path: string
  webUrl: string
}

export interface RiskReliefDeviation {
  searchReviewId: string
  searchQueryId: string
  searchResultId: string
  deviationStatus?: WorkspaceDocumentSetStatus
  summary?: string
  urn?: string
}

export interface QueryDeviation {
  searchReviewId: string
  searchQueryId: string
  deviationStatus?: WorkspaceDocumentSetStatus
  deviationStatusChangedAt?: string
}
export interface WorkspaceRiskReliefDeviationConfig {
  searchReviewIds: string[]
  queryDeviations: QueryDeviation[]
  deviations: RiskReliefDeviation[]
}

export interface ConformityChecksConfigDocumentSet {
  id: string
  path?: string
  webUrl?: string
  title?: string
  creationDate?: string
  documentSetStatus?: WorkspaceDocumentSetStatus
  ownerId?: string
}

export interface WorkspaceConformityChecksConfig {
  documents: ConformityChecksConfigDocumentSet[]
  flaggedDeviations?: ConformityCheckQualityReviewItem[]
}

export interface WorkspaceLead {
  id: string
  name: string
}

export interface WorkspaceConfig {
  excludedMissingReference?: string[]
  ofrDocumentTemplates?: string
  ofrDocumentTemplatesName?: string
  ofrDocumentTemplatesId?: string
  riskReliefConfig?: WorkspaceRiskReliefDeviationConfig
  conformityChecksConfig?: WorkspaceConformityChecksConfig
  workspaceLead?: WorkspaceLead
}

export interface WorkspaceFolder {
  path: string
  folders: WorkspaceFolder[]
  files: WorkspaceFiles[]
}

export interface WorkspaceFolderResponse {
  filesInStructure: WorkspaceFiles[]
  folderStructure: WorkspaceFolder
}

export interface ReferBy {
  id: string
  referById: string
  title: string
  url: string
}

export interface MissingReference {
  missing: string
  missingFile: string
  category: MissingReferenceCategory
  referBy: ReferBy[]
}

export interface ReferenceByDoc {
  docId: string
  docTitle: string
  urls: string
  refs: string[]
  totalTokens: number
}

export interface RefAIRunResult {
  missingReferences: MissingReference[]
  internalReferencesMissing: string[]
  externalReferencesMissing: string[]
  irrelevantReferencesMissing: string[]
  internalClusters?: string
  externalClusters?: string
  referencesByDoc: ReferenceByDoc[]
  totalTokensProcessed?: number
  totalDocuments: number
  processedDocument: number
  numberOfReferrers: number
}

export interface RiskReliefDeviationClassificationGlobal {
  detectability: RiskReliefDeviationSeverity
  occurrence: RiskReliefDeviationSeverity
  severity: RiskReliefDeviationSeverity
  risk: RiskReliefDeviationRisk
}

export interface RiskReliefDeviationClassification extends RiskReliefDeviationClassificationGlobal {
  id?: string
  identifiedRisk?: string
}

export interface RiskReliefDeviationClassificationRunResult {
  deviationClassification?: RiskReliefDeviationClassification[]
  deviationClassificationGlobal?: RiskReliefDeviationClassificationGlobal
}

export interface RiskReliefRootCauseDetails {
  id: string
  urn: string
  rootCauseSummary: string
  documentId: string
}

export interface RiskReliefRootCause {
  count: number
  score: number
  details?: RiskReliefRootCauseDetails[]
}

export interface RiskReliefRootCauseDiscoveryRunResult {
  [key: string]: RiskReliefRootCause
}

export interface CAPAGeneration {
  id: string
  rootCause: string
  correctiveAction: string
  preventiveAction: string
}

export interface ConformityCheckQualityReviewItem {
  id: string
  key: string
  isDeviation?: boolean
  pageNum: number | string
  ofrBlockType: OfrBlockType
  ofrBlockName: string
  status: string
  qualityReviewDeviation: QualityReviewDeviationType
  algorithmRunId?: string
  documentId?: string
  isFlaggedDeviation?: boolean
}

export interface RiskReliefCapaGenerationRunResult {
  capaGenerations: CAPAGeneration[]
}

export interface RegulatoryItemLocation {
  linkId: string
  linkPath: string
}

export interface RegulatoryItem {
  regulatoryExpectation: string
  locations: RegulatoryItemLocation[]
}

export interface RegulatorySubmissionRunResult {
  absent: string[]
  present: RegulatoryItem[]
}

export interface OfrAnnotationFormPage {
  id: string
  name: string
  type: OfrBlockType
  pageNumber: number
}

export interface OfrDocument {
  id: string
  name: string
  deviations: number
  formPages: OfrAnnotationFormPage[] // renamed
}

export interface DeviatingOfrBlock extends OfrBlockInfo {
  name: string
  qualityReviewDeviation?: QualityReviewDeviationType
  pageNum?: number
  id: string
  ofrBlockType?: OfrBlockType
  ofrBlockName?: string
  status?: string
  deviation?: boolean
}

export interface ConformityCheckLabQRRunResult {
  [key: string]: DeviatingOfrBlock
}

export interface QualityReviewRunResult {
  [fileName: string]: DeviatingOfrBlock[]
}

export interface OfrRunResult {
  documents: OfrDocument[]
}

export interface AlgorithmRunDelConfig {
  // this will be updated once backend is ready
  [key: string]: any
}

export interface DelRunResult {
  // this will be updated once backend is ready
  [key: string]: any
}

export interface AlgorithmRunConformityCheckConfig {
  documentTemplateId?: string
  documentTemplateName?: string
  ofrTemplateId?: string
  documentObjectId?: string
  documentTargetId?: string
  comparisonRunId?: string // last run id
  iterationFilename?: string
}

export interface ConformityCheckConfigDocument {
  id: string
  path: string
  title: string
  webUrl: string
  ownerId: string
  creationDate: string
  documentSetStatus: WorkspaceDocumentSetStatus
}

export interface ConformityCheckConfig {
  conformityCheckConfig?: {
    documents: ConformityCheckConfigDocument[]
  }
}

export interface BiomarkersFeatureData {
  index: number
  columnName: string
  importance: number
  importancePercentage: number
}

export interface BiomarkersRunResult {
  bestMask: number[]
  featureRanking: BiomarkersFeatureData[]
  f1Score: number
}

export interface AlgorithmRunBiomarkConfig {
  maxScore?: number
  trials?: number
  testPercent?: number
  generations?: number
  population?: number
  mutationRate?: number
  numMutants?: number
  numXovers?: number
  numRefines?: number
  modelType?: BiomarkModelType
  language?: string
}

export interface AlgorithmRunRegulatorySubmissionConfig {
  tableOfContentIds: string[]
  fileIds?: string[]
  documentNames?: string[]
}

export type CellCartaConformityCheckStatus =
  | 'FLAG_IGNORED'
  | 'COLUMN_IGNORED'
  | undefined

export interface CellCartaMEtadataConformityCheckColumn {
  columnName: string
  suggestedValue: string
  value: string
  pattern: string
  status: CellCartaConformityCheckStatus
}

export interface CellCartaMetadataConformityCheckSample {
  id: string
  invId: string
  columns: CellCartaMEtadataConformityCheckColumn[]
}

export interface CellCartaMetadataConformityCheckRunResult {
  samples: CellCartaMetadataConformityCheckSample[]
}

export interface CompensationMatrixAnalysisMatchingRunResultExperiment {
  id?: string
  fileName?: string
  cytometer?: string
  compensation?: string
  validationStatus?: string
  validationReason?: string
  experimentName?: string
  confidenceScore?: number
}

export interface CompensationMatrixAnalysisFilteringRunResultExperiment {
  id?: string
  src?: string
  cytometer?: string
  experimentName?: string
  compensationApplied?: string
  marker?: string
  fluorochrome?: string
}

export interface AlgorithmRunCompensationMatrixAnalysisConfig {
  ssr?: string
  experimentName?: string
  manuallySelectExperimentRun?: boolean
  selectedExperimentRuns?: string[]
}

export interface FlowCytometryReviewAnalysisRunMetadata {
  runDate: string
  analysisExperimentId: string
  ssrExperimentId: string
  ssrFilename: string
  attachmentsExperimentIds: string[]
  totalFlags: number
}

export interface ReviewAnalysisGateReviewRow {
  patientId: string
  filename: string
  visit: string
  exportTime: string
  gateName: string
  parentPopulation: string
  deviationPercent: number
  flagMessage: string
  standardFileRef: string
  severity?: FlowCytometryReviewAnalysisSeverity
}

export interface ReviewAnalysisCompensationMatrixReviewRow {
  patientId: string
  filename: string
  cytometerId: string
  matrixNameFound: string
  flagMessage: string
  severity?: FlowCytometryReviewAnalysisSeverity
}

export interface ReviewAnalysisGateLockReviewRow {
  patientId: string | null
  filename: string
  gatesLocked?: boolean
  severity?: FlowCytometryReviewAnalysisSeverity
}

export interface ReviewAnalysisAdditionalFileReviewRow {
  attachmentName: string
  checkType: string
  flagMessage: string
}

export interface ReviewAnalysisOtherIssue {
  message: string
}

export interface FlowCytometryReviewAnalysisRunResult {
  metadata: FlowCytometryReviewAnalysisRunMetadata
  gateReview: ReviewAnalysisGateReviewRow[]
  compensationMatrixReview: ReviewAnalysisCompensationMatrixReviewRow[]
  gateLockReview: ReviewAnalysisGateLockReviewRow[]
  additionalFileReview: ReviewAnalysisAdditionalFileReviewRow[]
  otherIssues: ReviewAnalysisOtherIssue[]
}

export type AlgorithmRunItemDetails = OfrDocument // Will contain many in the future "OfrDocument | Another | AnotherAgain

export interface AlgorithmRunRiskReliefConfig {
  deviationId: string
  deviations: RiskReliefDeviationInput[]
  rootCauseInclusions?: Record<string, string[]>
}

export interface RiskReliefDeviationClassificationResult {
  deviationClassification: RiskReliefDeviationClassification[]
  deviationClassificationGlobal: RiskReliefDeviationClassificationGlobal
}

export type AlgorithmRunResult =
  | RefAIRunResult
  | BiomarkersRunResult
  | RegulatorySubmissionRunResult
  | DelRunResult
  | OfrRunResult
  | QualityReviewRunResult
  | RiskReliefDeviationClassificationRunResult
  | CellCartaMetadataConformityCheckRunResult

export interface RiskReliefDeviationInput {
  id: string
  urn: string
}

export interface SmartFunctionRunStatus {
  [key: string]: AlgorithmRunStatus
}

export type AlgorithmRunConfig =
  | AlgorithmRunBiomarkConfig
  | AlgorithmRunRegulatorySubmissionConfig
  | AlgorithmRunDelConfig
  | AlgorithmRunConformityCheckConfig
  | AlgorithmRunRiskReliefConfig
  | AlgorithmRunCompensationMatrixAnalysisConfig

export interface RiskReliefNodeResult {
  DEVIATION_CLASSIFICATION: RiskReliefDeviationClassificationResult
  ROOT_CAUSE_DISCOVERY: RiskReliefRootCauseDiscoveryRunResult
  CAPA_GENERATION: RiskReliefCapaGenerationRunResult
}

export interface FlowCytometryNodeResult {
  COMPENSATION_MATRIX_ANALYSIS_MATCHING?: CompensationMatrixAnalysisMatchingRunResultExperiment[]
  COMPENSATION_MATRIX_ANALYSIS_FILTERING?: CompensationMatrixAnalysisFilteringRunResultExperiment[]
  REVIEW_ANALYSIS: FlowCytometryReviewAnalysisRunResult
}

export type NodeResults = RiskReliefNodeResult | FlowCytometryNodeResult

export interface NodeStatuses {
  DEVIATION_CLASSIFICATION?: AlgorithmRunStatus
  ROOT_CAUSE_DISCOVERY?: AlgorithmRunStatus
  CAPA_GENERATION?: AlgorithmRunStatus
  PROTOCOL_EXECUTION?: AlgorithmRunStatus
  QUALITY_REVIEW?: AlgorithmRunStatus
  OFR_FILL?: AlgorithmRunStatus
  LABQR?: AlgorithmRunStatus
  OFR_FILING?: AlgorithmRunStatus
}

export interface AlgorithmRun {
  id: string
  workspace?: string
  dataSource?: string
  runStatus?: AlgorithmRunStatus
  outputPath?: string
  runResult?: AlgorithmRunResult
  nodeResults?: NodeResults
  nodeStatuses?: NodeStatuses
  actionBy?: User
  actionDate?: string
  completedDate?: string
  config?: AlgorithmRunConfig
  resultPath?: string
}

export interface RiskReliefAlgorithmRun extends AlgorithmRun {
  smartFunctionType?: string
}

interface WorkflowNode {
  id: string
  functionName: string
  breakpoint?: boolean
}

interface WorkflowLink {
  source: string
  target: string
}

export interface WorkflowTemplate {
  nodes: WorkflowNode[]
  links: WorkflowLink[]
}

export type WorkspaceTypeCode =
  | 'REFAI'
  | 'OFR_QR' // OFR (Optical form reader) with quality review
  | 'BIOMARK' // Biomarkers
  | 'DEL' // Drug Establishment License (Pharmascience)
  | 'REGSUB' // Regulatory Submission (World Medicine)
  | 'TRMATRIX' // Traceability Matrix
  | 'RISK_REL' // Risk relief
  | 'CC_FC' // Cell Carta Flow Cytometry
  | 'CC_MSD' // Cell Carta MSD/OLink / Data aggregator
  | 'CC_MCC' // Cell Carta Metadata Conformity Check

export interface WorkspaceType {
  id: string
  algorithm_url: string
  createdBy: string
  createdDate: string
  lastModifiedBy: string
  lastModifiedDate: string
  contributors: string[]
  typeCode: string
  typeName: string
}

interface WorkspaceBase {
  id: string
  clusterId?: string | null
  externalId?: string | null
  createdBy?: string
  createdDate?: string
  lastModifiedBy?: string
  lastModifiedDate?: string
  title: string
  description?: string
  workspaceType?: string
  typeName: string
  typeCode: WorkspaceTypeCode
  algorithmUrl?: string
  visibility: PrivacyType
  algorithmRuns?: AlgorithmRun[]
  algorithmName?: string
  config?: WorkspaceConfig
  uiFlags?: number
}

export interface Workspace extends WorkspaceBase {
  dataSource: string
  dataStores?: string[]
  participants?: string[]
}

export interface WorkspaceDetails extends WorkspaceBase {
  dataSource: DataSource
  dataStores?: DataStore[]
  participants?: User[]
}

export interface WorkspaceState {
  workspaceList?: Workspace[]
  totalResults?: number
  workspaceDetailsLoading?: boolean
  selected?: WorkspaceDetails
  workspaceTypeList?: WorkspaceType[]
  selectedAlgorithmRun?: AlgorithmRun
  selectedAlgorithmRunItemDetails?: AlgorithmRunItemDetails
  loading?: boolean
  folders?: WorkspaceFolder[]
  files?: WorkspaceFiles[]
  workspaceFilesLoading?: boolean
  algorithmRunCsv?: string
  algorithmRunListCsv?: string
  listCsv?: string
  detailsCsv?: string
  workspaceDataSourceListCsv?: string
  workspaceDataStoreListCsv?: string
  workspaceParticipantListCsv?: string
  uiFlags?: number
}

export enum SmartFunctionResultLinkModel {
  LABQR = 'labqr',
  DEFAULT = 'default',
}

export interface NotifyUsersOfSmartFunctionResultsBody {
  sendTo: string[]
  subject?: string
  resultLinkModel?: SmartFunctionResultLinkModel
  emailTemplate?: string
  emailParams?: Record<string, string | number>
}

const initialState: WorkspaceState = {
  workspaceList: [],
  workspaceDetailsLoading: false,
  selected: undefined,
  workspaceTypeList: undefined,
  selectedAlgorithmRun: undefined,
  selectedAlgorithmRunItemDetails: undefined,
  loading: false,
  folders: undefined,
  files: undefined,
  workspaceFilesLoading: false,
  algorithmRunCsv: undefined,
  algorithmRunListCsv: undefined,
  listCsv: undefined,
  detailsCsv: undefined,
  workspaceDataSourceListCsv: undefined,
  workspaceDataStoreListCsv: undefined,
  workspaceParticipantListCsv: undefined,
  totalResults: 0,
  uiFlags: undefined,
}

const workspaceSlice = createSlice({
  name: 'workspaces',
  initialState,
  reducers: {
    setWorkspaceList: (state, action: PayloadAction<Workspace[]>) => {
      state.workspaceList = action.payload
    },
    setWorkspaceTotalResults: (state, action: PayloadAction<number>) => {
      state.totalResults = action.payload
    },
    setSelectedWorkspace: (state, action: PayloadAction<WorkspaceDetails>) => {
      state.selected = action.payload
    },
    setWorkspaceTypeList: (state, action: PayloadAction<WorkspaceType[]>) => {
      state.workspaceTypeList = action.payload
    },
    setSelectedAlgorithmRun: (state, action: PayloadAction<AlgorithmRun>) => {
      state.selectedAlgorithmRun = action.payload
    },
    setSelectedAlgorithmRunItemDetails: (
      state,
      action: PayloadAction<AlgorithmRunItemDetails>,
    ) => {
      state.selectedAlgorithmRunItemDetails = action.payload
    },
    clearSelectedAlgorithmRun: (state) => {
      state.selectedAlgorithmRun = undefined
    },
    setWorkspaceLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setWorkspaceDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.workspaceDetailsLoading = action.payload
    },
    setAlgorithmRunListCsv: (state, action: PayloadAction<string>) => {
      state.algorithmRunListCsv = action.payload
    },
    setAlgorithmRunCsv: (state, action: PayloadAction<string>) => {
      state.algorithmRunCsv = action.payload
    },
    setWorkspaceListUiFlags: (state, action: PayloadAction<number>) => {
      state.uiFlags = action.payload
    },
    setWorkspaceListCsv: (state, action: PayloadAction<string>) => {
      state.listCsv = action.payload
    },
    setWorkspaceDetailsCsv: (state, action: PayloadAction<string>) => {
      state.detailsCsv = action.payload
    },
    setWorkspaceDataSourceListCsv: (state, action: PayloadAction<string>) => {
      state.workspaceDataSourceListCsv = action.payload
    },
    setWorkspaceDataStoreListCsv: (state, action: PayloadAction<string>) => {
      state.workspaceDataStoreListCsv = action.payload
    },
    setWorkspaceParticipantListCsv: (state, action: PayloadAction<string>) => {
      state.workspaceParticipantListCsv = action.payload
    },
    setWorkspaceFilesLoading: (state, action: PayloadAction<boolean>) => {
      state.workspaceFilesLoading = action.payload
    },
    setWorkspaceFolders: (
      state,
      action: PayloadAction<WorkspaceFolder[] | undefined>,
    ) => {
      state.folders = action.payload
    },
    setWorkspaceFiles: (state, action: PayloadAction<WorkspaceFiles[]>) => {
      state.files = action.payload
    },
    clearWorkspaceFolders: (state) => {
      state.folders = undefined
      state.files = undefined
      state.workspaceFilesLoading = false
    },
    clearWorkspaceState: () => initialState,
  },
})

export const {
  clearSelectedAlgorithmRun,
  clearWorkspaceState,
  clearWorkspaceFolders,
  setAlgorithmRunCsv,
  setAlgorithmRunListCsv,
  setSelectedAlgorithmRun,
  setSelectedAlgorithmRunItemDetails,
  setSelectedWorkspace,
  setWorkspaceTypeList,
  setWorkspaceDataSourceListCsv,
  setWorkspaceDataStoreListCsv,
  setWorkspaceDetailsCsv,
  setWorkspaceDetailsLoading,
  setWorkspaceFolders,
  setWorkspaceFiles,
  setWorkspaceList,
  setWorkspaceListCsv,
  setWorkspaceLoading,
  setWorkspaceParticipantListCsv,
  setWorkspaceFilesLoading,
  setWorkspaceTotalResults,
  setWorkspaceListUiFlags,
} = workspaceSlice.actions

export const getWorkspaces = (state: RootState) => state.workspace.workspaceList

const workspaceReducer = workspaceSlice.reducer
export default workspaceReducer
